import React from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import UserModalSelect from './UserModalSelect';
import DateCurrentInput from '../inputs/DateCurrentInput';

interface Props {
  nextNumber: string;
  rnWorkOrderNumber?: string;
  subject: string;
  onSubjectChange: (value: string) => void;
  dateTo: string;
  onDateToChange: (value: string) => void;
  recipients: string;
  onRecipientsChange: (value: string) => void;
  text: string;
  onTextChange: (value: string) => void;
  canSend: boolean;
  submitting: boolean;
  onSubmit: () => void;
}

const RnQpCreateForm: React.FC<Props> = ({
  nextNumber,
  rnWorkOrderNumber,
  subject,
  onSubjectChange,
  dateTo,
  onDateToChange,
  recipients,
  onRecipientsChange,
  text,
  onTextChange,
  canSend,
  submitting,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-3 rounded-md border border-gray-200 p-3">
      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-700">{t('rnQpRequests.requestNumber')}</label>
        <div className="flex h-9 items-center rounded-md border border-blue-200 bg-blue-50 px-3 text-sm font-semibold text-blue-800">
          {nextNumber || (rnWorkOrderNumber ? `${rnWorkOrderNumber}/QP/01` : '—')}
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-700">
          {t('rnQpRequests.subject')} <span className="text-red-500">*</span>
        </label>
        <input
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          maxLength={200}
          className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
        />
      </div>
      <div className="max-w-xs">
        <label className="mb-1 block text-xs font-semibold text-gray-700">{t('rnQpRequests.dueDate')}</label>
        <DateCurrentInput
          value={dateTo}
          onChange={onDateToChange}
          showIcon
          inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-800 bg-white focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-700">
          {t('rnQpRequests.recipients')} <span className="text-red-500">*</span>
        </label>
        <UserModalSelect value={recipients} onChange={onRecipientsChange} placeholder={t('rnQpRequests.recipients') || undefined} />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-700">{t('rnQpRequests.text')}</label>
        <textarea
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          rows={4}
          maxLength={4000}
          className="w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
        />
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSend}
          className={`inline-flex h-9 items-center gap-2 rounded-md px-4 text-xs font-semibold ${
            canSend ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/50' : 'cursor-not-allowed bg-gray-200 text-gray-400'
          }`}
        >
          <Send size={15} />
          {submitting ? '...' : t('rnQpRequests.send')}
        </button>
      </div>
    </div>
  );
};

export default RnQpCreateForm;
